import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, Clock, Check, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS_PL = ["Styczeń","Luty","Marzec","Kwiecień","Maj","Czerwiec","Lipiec","Sierpień","Wrzesień","Październik","Listopad","Grudzień"];
const DAYS_PL = ["Pon","Wt","Śr","Czw","Pt","Sob","Nie"];

function getCalendarDays(year, month) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startDay = (first.getDay() + 6) % 7;
  const days = [];
  for (let i = 0; i < startDay; i++) days.push(null);
  for (let d = 1; d <= last.getDate(); d++) days.push(d);
  return days;
}

const FadeUp = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay }} className={className}>
      {children}
    </motion.div>
  );
};

function StepBar({ step }) {
  const STEPS = [
    { n: 1, l: "Usługa" },
    { n: 2, l: "Wariant" },
    { n: 3, l: "Data" },
    { n: 4, l: "Godzina" },
    { n: 5, l: "Dane" },
  ];
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {STEPS.map((s, i) => (
        <div key={s.n} className="flex items-center gap-2">
          <div className={`w-7 h-7 flex items-center justify-center text-xs font-outfit font-semibold flex-shrink-0 transition-all ${step > s.n ? "bg-violet-600 text-white" : step === s.n ? "bg-violet-600 text-white shadow-glow-violet" : "border border-white/20 text-white/30"}`}>
            {step > s.n ? <Check size={12} /> : s.n}
          </div>
          <span className={`text-xs uppercase tracking-[0.12em] hidden sm:inline ${step >= s.n ? "text-white/80" : "text-white/25"}`}>{s.l}</span>
          {i < STEPS.length - 1 && <div className="w-4 h-px bg-white/10 hidden sm:block" />}
        </div>
      ))}
    </div>
  );
}

export default function Booking() {
  const today = new Date();
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);      // {index, label, price, duration}
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ client_name: "", client_email: "", client_phone: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/pricing`).then(r => setServices(r.data)).catch(console.error);
  }, []);

  useEffect(() => {
    if (selectedRow) {
      axios.get(`${API}/slots/dates`).then(r => setAvailableDates(r.data)).catch(console.error);
    }
  }, [selectedRow]);

  

  const handleServiceSelect = (svc) => {
    const isNew = svc.id !== selectedService?.id;
    setSelectedService(svc);
    setSelectedRow(null);
    setSelectedSlot(null);
    setSlots([]);
    if (isNew) setSelectedDate(null);
    setStep(2);
  };

  

  const handleRowSelect = (row, idx) => {
    const duration = selectedService?.durations?.[idx] || 60;
    const newRow = { index: idx, label: row.label, price: row.price, duration };
    setSelectedRow(newRow);
    setSelectedSlot(null);
    setSlots([]);
    if (selectedDate) {
      fetchSlots(selectedDate, selectedService.id, idx);
    }
    setStep(3);
  };

  const fetchSlots = async (dateStr, svcId, rowIdx) => {
    setSlotsLoading(true);
    try {
      const { data } = await axios.get(`${API}/slots?date=${dateStr}&service_id=${svcId}&row_index=${rowIdx}`);
      setSlots(data);
    
    } catch (e) { console.error(e); }
    finally { setSlotsLoading(false); }
  };

  const handleDateSelect = async (day) => {
    if (!day) return;
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const isPast = new Date(dateStr) < new Date(today.toDateString());
    if (isPast || !availableDates.includes(dateStr)) return;
    setSelectedDate(dateStr);
    setSelectedSlot(null);
    await fetchSlots(dateStr, selectedService.id, selectedRow.index);
    setStep(4);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!selectedSlot) { setError("Wybierz termin."); return; }
    setLoading(true); setError("");
    try {
        await axios.post(`${API}/bookings`, {
        service_id: selectedService?.id || "",
        service_name: selectedService?.subtitle || "",
        row_index: selectedRow?.index || 0,
        row_label: selectedRow?.label || "",
        row_price: selectedRow?.price || "",
        slot_id: selectedSlot.id,
        date: selectedDate,
        time: selectedSlot.start_time,
        ...form,
      });
      setStep(6);
    } catch (err) {
      setError(err.response?.data?.detail || "Błąd. Spróbuj ponownie.");
    } finally { setLoading(false); }
  };

  const reset = () => {
    setStep(1); setSelectedService(null); setSelectedRow(null);
    setSelectedDate(null); setSelectedSlot(null); setSlots([]);
    setForm({ client_name: "", client_email: "", client_phone: "", notes: "" });
    setError("");
  };

  const days = getCalendarDays(year, month);
  const isAvail = (d) => {
    if (!d) return false;
    const s = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
    return availableDates.includes(s) && new Date(s) >= new Date(today.toDateString());
  };
  const isSel = (d) => {
    if (!d) return false;
    return `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}` === selectedDate;
  };
  const isPast = (d) => {
    if (!d) return false;
    return new Date(`${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`) < new Date(today.toDateString());
  };
  const prevMonth = () => { if (month === 0) { setYear(y=>y-1); setMonth(11); } else setMonth(m=>m-1); };
  const nextMonth = () => { if (month === 11) { setYear(y=>y+1); setMonth(0); } else setMonth(m=>m+1); };

  return (
    <div className="bg-brand-base min-h-screen">
      <Navbar />

      <section className="relative min-h-[16vh] md:min-h-[22vh] flex items-end pt-24 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div style={{ background: "radial-gradient(circle at 40% 50%, rgba(79,70,229,0.08) 0%, transparent 60%)" }} className="absolute inset-0" />
        </div>
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 pb-10 w-full relative">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="font-outfit text-xs uppercase tracking-[0.3em] text-violet-400 mb-3">Zarezerwuj termin</p>
            <h1 className="font-anton text-5xl sm:text-7xl text-white uppercase leading-none">REZERWACJA</h1>
          </motion.div>
        </div>
      </section>

      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 pt-8 pb-2">
        <StepBar step={step > 5 ? 5 : step} />
      </div>

      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-8 pb-24">

        {/* SUCCESS */}
        {step === 6 && (
          <FadeUp>
            <div data-testid="booking-success" className="max-w-lg mx-auto text-center py-14">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-8"
                style={{ background: "rgba(139,0,255,0.15)", border: "1px solid rgba(139,0,255,0.3)" }}>
                <Check size={36} className="text-violet-500" />
              </div>
              <h2 className="font-anton text-4xl text-white uppercase mb-4">Prośba Wysłana!</h2>
              <p className="text-white/60 text-sm mb-1">Usługa: <span className="text-white">{selectedService?.subtitle}</span></p>
              <p className="text-white/60 text-sm mb-1">Wariant: <span className="text-white">{selectedRow?.label} — {selectedRow?.price}</span></p>
              <p className="text-white/60 text-sm mb-1">Termin: <span className="text-white">{selectedDate} o {selectedSlot?.start_time}</span></p>
              <p className="text-white/60 text-sm mb-6">E-mail: <span className="text-white">{form.client_email}</span></p>
              <div className="border border-violet-600/30 p-4 mb-8 text-left" style={{ background: "rgba(139,0,255,0.05)" }}>
                <div className="text-xs uppercase tracking-[0.15em] text-violet-400 mb-2">Oczekuje na potwierdzenie</div>
                <p className="text-white/60 text-xs leading-relaxed">Milena potwierdzi rezerwację w ciągu 24 godzin.</p>
              </div>
              <button data-testid="booking-new" onClick={reset} className="btn-primary">
                Nowa Rezerwacja <ArrowRight size={14} />
              </button>
            </div>
          </FadeUp>
        )}

        {step < 6 && (
          <div className="grid lg:grid-cols-2 gap-10">
            <div className="space-y-6">

              {/* STEP 1 — SERVICE SELECTION */}
              <FadeUp>
                <div className="border border-white/8 p-6" style={{ background: "#0A0A0A" }}>
                  <h3 className="font-outfit text-xs uppercase tracking-[0.2em] text-white/40 mb-4">
                    Krok 1 — Wybierz usługę
                  </h3>
                  <div className="space-y-1 max-h-80 overflow-y-auto pr-1">
                    {services.map((s) => {
                      const isConsultation =
                        s.subtitle?.toLowerCase().includes("konsultacja");

                      return (
                        <button
                          key={s.id}
                          data-testid={`select-service-${s.id}`}
                          onClick={() => handleServiceSelect(s)}
                          className={`
                            relative group w-full px-4 py-3
                            transition-all border flex justify-between items-center
                            text-sm text-left overflow-hidden

                            ${
                              isConsultation
                                ? `
                                  border-violet-500/60
                                  bg-violet-500/[0.08]
                                  text-white
                                  hover:bg-violet-500/[0.14]
                                  hover:border-violet-400
                                  shadow-[0_0_20px_rgba(139,92,246,0.08)]
                                `
                                : `
                                  border-white/5
                                  text-white/60
                                  hover:text-white
                                  hover:border-white/15
                                  hover:bg-white/[0.03]
                                `
                            }
                          `}
                        >
                          {isConsultation && (
                            <span className="absolute left-0 top-2 bottom-2 w-[2px] bg-violet-500" />
                          )}

                          <span className="font-outfit leading-tight">
                            {s.subtitle}

                            {isConsultation && (
                              <span className="
                                ml-3 inline-flex items-center
                                px-2 py-0.5
                                text-[9px] uppercase tracking-[0.15em]
                                text-violet-300
                                border border-violet-500/30
                                bg-violet-500/10
                              ">
                                Na start
                              </span>
                            )}
                          </span>

                          {selectedService?.id === s.id && (
                            <Check
                              size={14}
                              className="text-violet-500 flex-shrink-0 ml-2"
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-4 pt-3 border-t border-white/5 flex justify-between text-xs text-white/30">
                    <span>Nie wiesz jaką wybrać?</span>
                    <Link to="/cennik" className="text-violet-400 hover:text-white transition-colors">Zobacz cennik →</Link>
                  </div>
                </div>
              </FadeUp>

              {/* STEP 2 — ROW/STOPIEŃ SELECTION */}
              {step >= 2 && selectedService && (
                <FadeUp delay={0.05}>
                  <div className="border border-white/8 p-6" style={{ background: "#0A0A0A" }}>
                    <h3 className="font-outfit text-xs uppercase tracking-[0.2em] text-white/40 mb-1">
                      Krok 2 — Wybierz wariant
                    </h3>
                    <p className="text-white/25 text-xs mb-4 font-outfit">
                      Wybierz stopień długości lub rodzaj usługi.{" "}
                      <Link to="/cennik" className="text-violet-400 hover:text-white transition-colors">Co to stopień? →</Link>
                    </p>
                    {selectedService.warning && (
                      <div className="flex items-start gap-2 mb-4 p-3 border border-yellow-500/20" style={{ background: "rgba(234,179,8,0.04)" }}>
                        <AlertTriangle size={13} className="text-yellow-400 flex-shrink-0 mt-0.5" />
                        <p className="text-yellow-200/70 text-xs leading-relaxed">{selectedService.warning}</p>
                      </div>
                    )}
                    <div className="space-y-1.5">
                      {selectedService.rows.map((row, i) => {
                        const dur = selectedService.durations?.[i] || 60;
                        const isSel = selectedRow?.index === i;
                        return (
                          <button key={i} data-testid={`select-row-${i}`}
                            onClick={() => handleRowSelect(row, i)}
                            className={`w-full text-left px-4 py-3.5 transition-all border flex justify-between items-center ${isSel ? "border-violet-600 bg-violet-600/10" : "border-white/5 hover:border-violet-600/30"}`}>
                            <div>
                              <span className="font-outfit font-medium text-sm text-white">{row.label}</span>
                              <span className="text-white/30 text-xs ml-2">
                                <Clock size={10} className="inline mr-1" />{Math.floor(dur / 60)}h {dur % 60 > 0 ? `${dur % 60}min` : ""}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`font-anton text-lg ${isSel ? "text-white" : "text-white/60"}`}>{row.price}</span>
                              {isSel && <Check size={14} className="text-violet-500" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {selectedService.notes.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/5">
                        {selectedService.notes.map((n, i) => (
                          <p key={i} className="text-white/25 text-xs">* {n}</p>
                        ))}
                      </div>
                    )}
                  </div>
                </FadeUp>
              )}

              {/* STEP 3 — CALENDAR */}
              {step >= 3 && selectedRow && (
                <FadeUp delay={0.05}>
                  <div data-testid="booking-calendar" className="border border-white/8 p-6" style={{ background: "#0A0A0A" }}>
                    <h3 className="font-outfit text-xs uppercase tracking-[0.2em] text-white/40 mb-4">
                      Krok 3 — Wybierz datę
                      <span className="text-violet-400/60 ml-2">({selectedRow.duration} min)</span>
                    </h3>
                    <div className="flex justify-between items-center mb-4">
                      <button data-testid="cal-prev" onClick={prevMonth} className="text-white/40 hover:text-white p-1 transition-colors"><ChevronLeft size={17} /></button>
                      <h4 className="font-anton text-base text-white uppercase">{MONTHS_PL[month]} {year}</h4>
                      <button data-testid="cal-next" onClick={nextMonth} className="text-white/40 hover:text-white p-1 transition-colors"><ChevronRight size={17} /></button>
                    </div>
                    <div className="grid grid-cols-7 gap-1 mb-1">
                      {DAYS_PL.map(d => <div key={d} className="text-center text-[10px] text-white/20 uppercase py-1">{d}</div>)}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                      {days.map((day, idx) => (
                        <button key={idx}
                          data-testid={day ? `cal-day-${day}` : undefined}
                          onClick={() => handleDateSelect(day)}
                          disabled={!day || isPast(day) || !isAvail(day)}
                          className={`aspect-square flex items-center justify-center text-sm transition-all rounded-sm
                            ${!day ? "invisible" : ""}
                            ${isSel(day) ? "bg-violet-600 text-white shadow-glow-violet" : ""}
                            ${isAvail(day) && !isSel(day) ? "text-white hover:bg-violet-600/20 cursor-pointer border border-violet-600/25" : ""}
                            ${(isPast(day) || (!isAvail(day) && day)) ? "text-white/15 cursor-not-allowed" : ""}
                          `}>
                          {day}
                        </button>
                      ))}
                    </div>
                    <div className="mt-3 flex items-center gap-4 text-xs text-white/30">
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 border border-violet-600/30 rounded-sm inline-block" /> Dostępna</span>
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-violet-600 rounded-sm inline-block" /> Wybrana</span>
                    </div>
                  </div>
                </FadeUp>
              )}

              {/* STEP 4 — TIME SLOTS */}
              {step >= 4 && selectedDate && (
                <FadeUp delay={0.05}>
                  <div data-testid="time-slots" className="border border-white/8 p-6" style={{ background: "#0A0A0A" }}>
                    <h3 className="font-outfit text-xs uppercase tracking-[0.2em] text-white/40 mb-4">
                      Krok 4 — Godzina — {selectedDate}
                    </h3>
                    {slotsLoading ? (
                      <div className="text-white/30 text-sm py-4 text-center">Ładowanie...</div>
                    ) : slots.length === 0 ? (
                      <p className="text-white/30 text-sm py-4 text-center">
                        Brak terminów dla tej usługi w wybranym dniu. Spróbuj inny dzień.
                      </p>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {slots.map(slot => (
                          <button key={slot.id} data-testid={`slot-${slot.start_time}`}
                            onClick={() => {
                              setSelectedSlot(slot);
                              setStep(5);

                              if (window.innerWidth >= 768) {
                                window.scrollTo({
                                  top: 0,
                                  behavior: "smooth",
                                });
                              }
                            }}
                            className={`py-3 text-sm font-outfit text-center transition-all border ${selectedSlot?.id === slot.id ? "border-violet-600 bg-violet-600/15 text-white" : "border-white/10 text-white/60 hover:border-violet-600/40 hover:text-white"}`}>
                            <Clock size={10} className="inline mr-1 mb-0.5" />
                            {slot.start_time}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </FadeUp>
              )}
            </div>

            {/* Right column */}
            <div>
              {/* STEP 5 — FORM */}
              {step >= 5 && selectedSlot && (
                <FadeUp delay={0.05}>
                  <div data-testid="booking-form-container" className="border border-white/8 p-8" style={{ background: "#0A0A0A" }}>
                    {/* Summary */}
                    <div className="border border-white/5 p-4 mb-6" style={{ background: "#111111" }}>
                      <p className="text-xs uppercase tracking-[0.15em] text-white/30 mb-2">Podsumowanie</p>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between"><span className="text-white/50">Usługa</span><span className="text-white text-right max-w-[60%] leading-tight">{selectedService?.subtitle}</span></div>
                        <div className="flex justify-between"><span className="text-white/50">Wariant</span><span className="text-white">{selectedRow?.label}</span></div>
                        <div className="flex justify-between"><span className="text-white/50">Cena</span><span className="text-violet-400 font-medium">{selectedRow?.price}</span></div>
                        <div className="flex justify-between"><span className="text-white/50">Data</span><span className="text-white">{selectedDate} o {selectedSlot.start_time}</span></div>
                        <div className="flex justify-between"><span className="text-white/50">Czas</span><span className="text-white/70">{selectedRow?.duration} min</span></div>
                      </div>
                    </div>

                    <h3 className="font-outfit text-xs uppercase tracking-[0.2em] text-white/40 mb-5">Krok 5 — Twoje dane</h3>
                    <form data-testid="booking-form" onSubmit={handleSubmit} className="space-y-5">
                      <div>
                        <label className="text-xs uppercase tracking-[0.12em] text-white/35 block mb-2">Imię i nazwisko *</label>
                        <input data-testid="booking-name" type="text" required value={form.client_name}
                          onChange={e => setForm({ ...form, client_name: e.target.value })}
                          className="input-dark" placeholder="Anna Kowalska" />
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-[0.12em] text-white/35 block mb-2">E-mail *</label>
                        <input data-testid="booking-email" type="email" required value={form.client_email}
                          onChange={e => setForm({ ...form, client_email: e.target.value })}
                          className="input-dark" placeholder="anna@example.com" />
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-[0.12em] text-white/35 block mb-2">Telefon *</label>
                        <input data-testid="booking-phone" type="tel" required value={form.client_phone}
                          onChange={e => setForm({ ...form, client_phone: e.target.value })}
                          className="input-dark" placeholder="+48 000 000 000" />
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-[0.12em] text-white/35 block mb-2">Uwagi</label>
                        <textarea data-testid="booking-notes" rows={3} value={form.notes}
                          onChange={e => setForm({ ...form, notes: e.target.value })}
                          className="input-dark resize-none" placeholder="Np. stan włosów, inne informacje..." />
                      </div>
                      {error && <p className="text-red-400 text-sm">{error}</p>}
                      <button data-testid="booking-submit" type="submit" disabled={loading}
                        className="btn-primary w-full justify-center disabled:opacity-50">
                        {loading ? "Wysyłanie..." : "Potwierdź Rezerwację"} <ArrowRight size={14} />
                      </button>
                    </form>
                  </div>
                </FadeUp>
              )}

              {/* Info hint */}
              {step < 5 && (
                <FadeUp delay={0.1}>
                  <div className="border border-white/5 p-8 sticky top-28" style={{ background: "#0A0A0A" }}>
                    <h3 className="font-outfit text-xs uppercase tracking-[0.2em] text-white/35 mb-5">Jak zarezerwować?</h3>
                    <div className="space-y-4">
                      {[
                        { n: "01", t: "Wybierz usługę", d: "Z pełnej listy usług z cennika." },
                        { n: "02", t: "Wybierz wariant", d: "Stopień (1/2/3) lub rodzaj usługi." },
                        { n: "03", t: "Wybierz datę", d: "Kalendarz uwzględnia czas trwania." },
                        { n: "04", t: "Wybierz godzinę", d: "Tylko terminy z wolnym blokiem." },
                        { n: "05", t: "Wpisz dane", d: "Imię, e-mail i gotowe!" },
                      ].map((s, i) => (
                        <div key={i} className={`flex gap-4 transition-opacity ${step > i ? "opacity-100" : "opacity-35"}`}>
                          <div className="font-anton text-2xl leading-none flex-shrink-0"
                            style={{ WebkitTextStroke: "1px rgba(139,0,255,0.3)", color: "transparent" }}>{s.n}</div>
                          <div>
                            <div className="text-white text-sm font-outfit font-medium">{s.t}</div>
                            <div className="text-white/35 text-xs mt-0.5">{s.d}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 border-t border-white/5 pt-4">
                      <Link to="/cennik" className="text-violet-400 text-xs hover:text-white transition-colors uppercase tracking-[0.15em]">
                        Pełny cennik z stopniami →
                      </Link>
                    </div>
                  </div>
                </FadeUp>
              )}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
